import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2 } from "lucide-react";
import { CartItem as CartItemType, useCart } from "@/contexts/CartContext";
import { AvailabilitySlots } from "@/components/meal/AvailabilitySlots";
import { OnDemandDatePicker } from "@/components/meal/OnDemandDatePicker";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface CartItemProps {
  item: CartItemType;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemoveItem: (id: string) => void;
}

export const CartItem = ({ item, onUpdateQuantity, onRemoveItem }: CartItemProps) => {
  const { updateScheduledTime } = useCart();
  const isOnDemand = item.preparation_type === 'on_demand';
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [mealImage, setMealImage] = useState<string | null>(null);

  // Transform availability slots for the AvailabilitySlots component
  const transformedSlots = item.availability_slots?.map(slot => ({
    date: slot.dateRange.from,
    endDate: slot.dateRange.to,
    slots: [`${slot.startTime} - ${slot.endTime}`],
    recurring: slot.recurring ? {
      days: slot.recurring.days,
      until: slot.recurring.until
    } : undefined
  })) || [];

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    
    // Comprehensive null and type checks for image
    const hasInvalidImageFormat = 
      item.image !== null && 
      typeof item.image === 'object' && 
      '_type' in (item.image as object);
    
    // Handle different possible image formats
    if (item.image) {
      if (typeof item.image === 'string') {
        setMealImage(item.image);
        console.log(`Using provided image URL for ${item.name}:`, item.image);
      } else if (hasInvalidImageFormat) {
        console.log(`Invalid image format for ${item.name}, fetching from database...`);
        fetchMealImage(item.id);
      }
    } else {
      console.log(`No image found for ${item.name}, fetching from database...`);
      fetchMealImage(item.id);
    }
  }, [item.id, item.image]);

  const fetchMealImage = async (mealId: string) => {
    try {
      const { data, error } = await supabase
        .from('meals')
        .select('image')
        .eq('id', mealId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching meal image:', error);
        setImageError(true);
        return;
      }
      
      if (data && data.image) {
        console.log(`Successfully fetched image for ${item.name}:`, data.image);
        setMealImage(data.image);
      } else {
        console.log(`No image found in database for meal ${item.name}`);
        setImageError(true);
      }
    } catch (error) {
      console.error('Exception when fetching meal image:', error);
      setImageError(true);
    }
  };

  const handleDateSelection = (date: string) => {
    if (isOnDemand && item.pickup_window_start && item.pickup_window_end) {
      const timeWindow = `${item.pickup_window_start} - ${item.pickup_window_end}`;
      updateScheduledTime(item.id, date, timeWindow);
    } else {
      updateScheduledTime(item.id, date, item.scheduledTime || '12:00');
    }
  };

  const handleTimeSelection = (time: string) => {
    if (item.scheduledDate) {
      updateScheduledTime(item.id, item.scheduledDate, time);
    }
  };

  const imageToDisplay = mealImage || '/placeholder.svg';

  return (
    <div className={`flex flex-col sm:flex-row gap-4 bg-card p-4 rounded-lg shadow-sm ${
      !item.chef_id ? 'border-2 border-destructive' : ''
    }`}>
      <div className="flex items-start gap-3 w-full">
        <div className="relative w-20 h-20 flex-shrink-0">
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-md" />
          )}
          {!imageError ? (
            <img
              src={imageToDisplay}
              alt={item.name}
              className={`w-full h-full object-cover rounded-md ${!imageLoaded ? 'opacity-0' : 'opacity-100'}`}
              onLoad={() => {
                console.log(`Image loaded successfully for ${item.name}: ${imageToDisplay}`);
                setImageLoaded(true);
              }}
              onError={(e) => {
                console.error(`Failed to load image for ${item.name}: ${imageToDisplay}`);
                setImageError(true);
                const img = e.target as HTMLImageElement;
                img.src = '/placeholder.svg';
              }}
            />
          ) : (
            <div className="w-full h-full bg-gray-300 rounded-md flex items-center justify-center text-xs text-gray-500">
              Image unavailable
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div className="space-y-1">
              <h3 className="font-semibold text-lg">{item.name}</h3>
              <p className="text-sm text-muted-foreground">Chef: {item.chef}</p>
              <p className="font-medium text-primary">${item.price.toFixed(2)}</p>
            </div>
            <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end mt-2 sm:mt-0">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onUpdateQuantity(item.id, Math.max(0, item.quantity - 1))}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-8 text-center">{item.quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onRemoveItem(item.id)}
                className="h-8 w-8 flex-shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 w-full">
        {isOnDemand ? (
          <OnDemandDatePicker
            config={{
              prep_time_hours: Number(item.prep_time_hours) || 24,
              cutoff_time: item.cutoff_time || "18:00",
              pickup_window_start: item.pickup_window_start || "09:00",
              pickup_window_end: item.pickup_window_end || "17:00",
              available_days: item.available_days || ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              max_daily_orders: Number(item.max_daily_orders) || 10
            }}
            selectedDate={item.scheduledDate}
            selectedTime={item.scheduledTime}
            onDateSelected={handleDateSelection}
            onTimeSelected={handleTimeSelection}
          />
        ) : (
          <AvailabilitySlots
            slots={transformedSlots}
            onSlotSelect={(date, time) => {
              if (date && time) {
                updateScheduledTime(item.id, date, time);
              }
            }}
            selectedDate={item.scheduledDate}
            selectedTime={item.scheduledTime}
          />
        )}
      </div>
    </div>
  );
};
